import { useCallback, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useIsFocused } from '@react-navigation/core';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { getBookmarks } from 'store/bookmarks/actions';
import { getDetails, setDescriptionLoaded } from 'store/details/actions';
import {
    selectCastAndCrew,
    selectDescriptionLoading,
    selectDetails,
    selectDetailsError,
    selectDetailsLoading,
    selectDetailsRequestStatus,
} from 'store/details/selectors';
import { requestIdle } from 'store/utils';

import { RetryAbortController } from 'utils/axios/nagra/utils.ts';
import { ERROR_CODE_A014 } from 'utils/error/errorCodes.ts';
import { isWeb } from 'utils/responsiveness';

import { DetailsRequestUrlType } from 'screens/Detail/types';

/**
 * The idea of the search is:
 *  - if series id is available - request all episodes by `seriesId` and find series program details data by
 *    `id`(for local catchup items) or `contentId` + `season` + `episode` localy, depends on what is available
 *  - if series id is not available - request program details data by
 *    `id`(for local catchup items) or `contentId` + filter locally by `editorial.technicals.CUStartTime === startTime`, depends on what is available;
 */
function useDetailsData(params: RouteNavigationParams[SCREEN.DETAILS]) {
    const {
        filterType,
        templateId,
        id,
        seriesId,
        channelId,
        editorialId,
        programId,
    } = params;

    const details = useSelector(selectDetails(templateId));
    const castAndCrew = useSelector(selectCastAndCrew(templateId));
    const detailsError = useSelector(selectDetailsError(templateId));
    const detailsLoading = useSelector(selectDetailsLoading(templateId));
    const descriptionLoading = useSelector(
        selectDescriptionLoading(templateId),
    );
    const detailsRequestStatus = useSelector(
        selectDetailsRequestStatus(templateId),
    );

    const previousValues = useRef<RouteNavigationParams[SCREEN.DETAILS]>({
        id: undefined,
        seriesId: undefined,
        channelId: undefined,
        editorialId: undefined,
        programId: undefined,
        filterType: DetailsRequestUrlType.PROGRAM,
    });
    const isFocused = useIsFocused();
    const abortController = useRef(new RetryAbortController());
    const prevErrorCode = useRef(detailsError?.code);

    const dispatch = useDispatch();

    const onDescriptionLoaded = useCallback(() => {
        if (templateId) {
            dispatch(setDescriptionLoaded({ templateId }));
        }
    }, [dispatch, templateId]);

    useEffect(() => {
        const paramsChanged =
            filterType !== previousValues.current.filterType ||
            id !== previousValues.current.id ||
            seriesId !== previousValues.current.seriesId ||
            channelId !== previousValues.current.channelId ||
            editorialId !== previousValues.current.editorialId ||
            programId !== previousValues.current.programId;

        if (
            (!detailsLoading || id !== previousValues.current.id) &&
            templateId &&
            paramsChanged &&
            (!isWeb || (details?.id && details.id !== id) || !details?.id)
        ) {
            dispatch(getBookmarks());
            dispatch(
                getDetails(
                    { ...params, templateId },
                    {
                        autoRetry: true,
                        abortController: abortController.current,
                        retryCount: 1,
                        retryDuration: 30 * 1000,
                    },
                ),
            );
        }

        previousValues.current = {
            id,
            seriesId,
            channelId,
            editorialId,
            programId,
            filterType,
        };
    }, [id, seriesId, channelId, editorialId, programId, filterType]);

    const onRetry = useCallback(() => {
        if (templateId) {
            abortController.current.abort();
            abortController.current = new RetryAbortController();

            dispatch(getBookmarks());
            dispatch(
                getDetails(
                    { ...params, templateId },
                    {
                        autoRetry: true,
                        abortController: abortController.current,
                        retryCount: 1,
                        retryDuration: 60 * 1000,
                    },
                ),
            );
        }
    }, [dispatch, params, templateId]);

    useEffect(() => {
        if (!isFocused) {
            abortController.current.abort();
            abortController.current = new RetryAbortController();
        }
    }, [isFocused]);

    useEffect(() => {
        if (
            prevErrorCode.current !== ERROR_CODE_A014 &&
            detailsError?.code === ERROR_CODE_A014 &&
            templateId
        ) {
            dispatch(
                getDetails(
                    { ...params, templateId, silently: true },
                    {
                        autoRetry: true,
                        abortController: abortController.current,
                        retryCount: 5,
                        retryDuration: 5 * 60 * 1000,
                    },
                ),
            );
        }

        prevErrorCode.current = detailsError?.code;
    }, [detailsError]);

    return {
        detailsLoading:
            detailsLoading ||
            !detailsRequestStatus ||
            (detailsRequestStatus && requestIdle(detailsRequestStatus)),
        detailsError,
        details,
        descriptionLoading,
        onDescriptionLoaded,

        onRetry,

        castAndCrew,
    };
}

export default useDetailsData;
